import React, { PropsWithChildren, useRef, useState } from "react";
import { ShareDrawer } from "@bloomberg/media-ui-icons";
import { OutlinedButton, Toast } from "@bloomberg/media-ui-core";
import { useFileImport } from "~hooks/useFileImport";
import type { ImportedData } from "~utils/fileTransfer";

export interface ImportButtonProps {
  onImport: (data: ImportedData) => void;
  /** Optional accept string for <input>, defaults to Excel+CSV */
  accept?: string;
  className?: string;
  title?: string;
  disabled?: boolean;
}

export function ImportButton(
  props: PropsWithChildren<ImportButtonProps>
): JSX.Element {
  const {
    onImport,
    accept = ".csv, .xlsx, .xls",
    className = "",
    title = "Import data",
    disabled = false,
    children,
  } = props;

  const importFile = useFileImport();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];

    if (!file) return;

    // Determine type by extension
    const ext = file.name.split(".").pop()?.toLowerCase();
    const type: "csv" | "xlsx" = ext === "csv" ? "csv" : "xlsx";

    try {
      const data = await importFile(file, type);

      onImport(data);
    } catch (err) {
      setError("Failed to import, please try again.");
    } finally {
      // reset input so same file can be re-selected if needed
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleImport = () => fileInputRef.current?.click();

  return (
    <div className={className}>
      <input
        type="file"
        ref={fileInputRef}
        accept={accept}
        className="hidden"
        onChange={handleFileChange}
      />
      <OutlinedButton
        className={className}
        Icon={<ShareDrawer />}
        iconPosition="left"
        onClick={handleImport}
        disabled={disabled}
        title={title}
        size="default"
        version="phoenix"
        fullWidth
      >
        {children}
      </OutlinedButton>
      <Toast
        className="absolute mt-2"
        maxSize="medium"
        variant="error"
        position={{ vertical: "bottom", horizontal: "center" }}
        open={Boolean(error)}
        inline
      >
        {error}
      </Toast>
    </div>
  );
}
